/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import Button from '@mui/material/Button'
import { LinkTeamMemberInput, MembershipType } from '__generatedTypes__/globalTypes'
import { InputField } from 'components/Input'
import { Tooltip } from 'components/Tooltip'
import { validateRequired } from 'utils/validation'
import { MembershipLevelSelect } from 'components/MembershipLevelSelect'
import { toast } from 'utils/toast'
import { searchTeamMember_searchTeamMember_items as SearchedTeamMember } from '__generatedTypes__/searchTeamMember'
import { AuthorSearch } from 'pagesNew/SuccessStory/Create/AuthorSearch'
import * as classes from './styles'

export type FormValues = {
  id: string
  title: string
  membershipLevel: MembershipType
  author: Partial<SearchedTeamMember>[]
  subject: { id: string } | null
  isLive: boolean
  order: number | null
}

export type CategoryInput = {
  title?: string | null
  isLive?: boolean | null
  order?: number | null
  subject?: { id: string } | null
  membershipLevel?: MembershipType | null
  author?: LinkTeamMemberInput | null
}

export type Props = {
  category: Category | string
  onSubmit: (category: CategoryInput) => Promise<void>
  onCancel?: () => void
  onRemove?: () => void
  defaultOrder?: number
  submitting?: boolean
}

export type Category = {
  id: string
  title: string
  membershipLevel: MembershipType
  author: Partial<SearchedTeamMember> | null
  subject: { id: string } | null
  isLive: boolean
  order: number | null
}

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

export const prepareForSubmit = (values: Partial<FormValues>): CategoryInput => ({
  title: values.title,
  subject: values.subject ? { id: values.subject.id } : undefined,
  membershipLevel: values.membershipLevel,
  author: values.author?.[0]?.id ? { id: values.author[0].id } : null,
  order: values.order,
  isLive: values.isLive ?? false,
})

export const getInitialValues = (category: Category | string, defaultOrder = 0): Partial<FormValues> => {
  if (typeof category === 'string') {
    return {
      subject: { id: category },
      order: defaultOrder,
    }
  }

  return {
    ...category,
    author: category.author ? [category.author] : [],
  }
}

export const Form = ({ category, onSubmit, onCancel, onRemove, defaultOrder = 0, submitting }: Props) => {
  const [initialValues, setInitialValues] = useState(getInitialValues(category, defaultOrder))
  const [isWarningVisible, setIsWarningVisible] = useState(false)

  useEffect(() => {
    setIsWarningVisible(false)
    setInitialValues(getInitialValues(category, defaultOrder))
  }, [category])

  const handleRemoveClick = useCallback(() => {
    if (!initialValues.id) {
      return
    }

    if (isWarningVisible) {
      onRemove?.()
    } else {
      setIsWarningVisible(true)
    }
  }, [isWarningVisible])

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const categoryData = prepareForSubmit(values)
        await onSubmit(categoryData)
      } catch (error) {
        toast.error('Failed to prepare data for submission')
      }
    },
    [onSubmit]
  )

  return (
    <FinalForm<FormValues>
      mutators={{ ...arrayMutators }}
      decorators={[focusOnErrors]}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, initialValues, submitting: formSubmitting }) => (
        <form onSubmit={handleSubmit} css={classes.content}>
          <InputField name="title" validate={validateRequired} label="Title" placeholder="Enter Title" fullWidth />

          <MembershipLevelSelect name="membershipLevel" validate={validateRequired} fullWidth />

          <AuthorSearch label="Author" name="author" validate={validateRequired} />

          <div css={classes.buttonsWrapper}>
            {!!onCancel && (
              <Button
                variant="outlined"
                color="blue"
                disabled={formSubmitting || submitting}
                size="large"
                onClick={onCancel}
                fullWidth
              >
                Cancel
              </Button>
            )}
            {!!onRemove &&
              (initialValues.isLive ? (
                <Tooltip title="You can't delete Category because Live is enabled" isNote arrow>
                  <Button variant="outlined" color="red" size="large" fullWidth disabled>
                    Delete
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  color="red"
                  disabled={formSubmitting || submitting}
                  size="large"
                  onClick={handleRemoveClick}
                  fullWidth
                >
                  Delete
                </Button>
              ))}
            <Button
              type="submit"
              variant="contained"
              color="blue"
              disabled={formSubmitting || submitting}
              size="large"
              fullWidth
            >
              {initialValues.id ? 'Update' : 'Create'}
            </Button>
          </div>

          {isWarningVisible && (
            <div css={classes.warning}>Are you sure you want to delete? If yes, click “Delete” again to delete.</div>
          )}
        </form>
      )}
    />
  )
}
